import { z } from "zod/v4";

export const REPORT_SUGGESTION_CATEGORIES = [
  "performance",
  "creative",
  "audiences",
  "lifecycle",
  "data-health",
] as const;
export type ReportSuggestionCategory =
  (typeof REPORT_SUGGESTION_CATEGORIES)[number];

export const REPORT_SUGGESTION_CADENCES = [
  "daily",
  "weekly",
  "monthly",
] as const;
export type ReportSuggestionCadence =
  (typeof REPORT_SUGGESTION_CADENCES)[number];

export const REPORT_SUGGESTION_TIME_PERIODS = ["l7", "l30", "quarter"] as const;
export const REPORT_SUGGESTION_COMPARE_AGAINST = [
  "previous-period",
  "yoy",
] as const;
export const REPORT_SUGGESTION_COMPARISONS = [
  ...REPORT_SUGGESTION_COMPARE_AGAINST,
  "none",
] as const;
export const REPORT_SUGGESTION_FORMATS = [
  "summary",
  "detailed",
  "exec-digest",
] as const;

export const MAX_REPORT_SUGGESTIONS = 10;

export const reportSuggestionQuestionSchema = z.object({
  question: z
    .string()
    .min(1)
    .max(180)
    .describe(
      "Short free-text question to ask the user before generating this report prompt. Ask only when the answer would materially change this card's instructions. Max 180 characters.",
    ),
  required: z
    .boolean()
    .describe(
      "Whether the user must answer this question before continuing with this suggestion.",
    ),
});
export type ParsedReportSuggestionQuestion = z.infer<
  typeof reportSuggestionQuestionSchema
>;

/**
 * Legacy defaults contract — the card structure that shipped before
 * `predefinedQuestions`. Retained only to parse suggestion rows persisted
 * before the merge; the frontend reconstructs `predefinedQuestions` from it.
 */
export const reportSuggestionDefaultsSchema = z.object({
  timePeriod: z
    .enum(REPORT_SUGGESTION_TIME_PERIODS)
    .describe("Default look-back window for the report."),
  channels: z
    .array(z.string())
    .describe(
      "Ad channels to pre-select, e.g. ['facebook','google']. Empty when not channel-specific.",
    ),
  compareAgainst: z
    .enum(REPORT_SUGGESTION_COMPARE_AGAINST)
    .nullable()
    .describe("Comparison baseline, or null when no comparison applies."),
  format: z
    .enum(REPORT_SUGGESTION_FORMATS)
    .describe("Default presentation format for the report."),
});
export type ReportSuggestionDefaults = z.infer<
  typeof reportSuggestionDefaultsSchema
>;

export const reportPredefinedQuestionsSchema = z.object({
  timePeriod: z
    .enum(REPORT_SUGGESTION_TIME_PERIODS)
    .nullable()
    .describe(
      "Default look-back window (l7/l30/quarter) to pre-select, or null to hide the Time period control.",
    ),
  channels: z
    .array(z.string())
    .nullable()
    .describe(
      "Ad channels to pre-select (e.g. ['facebook','google']; [] shows the control with none pre-selected), or null to hide the Channels control.",
    ),
  comparison: z
    .enum(REPORT_SUGGESTION_COMPARISONS)
    .nullable()
    .describe(
      "Default comparison baseline ('previous-period'/'yoy', or 'none' to default to no comparison), or null to hide the Compare to control.",
    ),
  format: z
    .enum(REPORT_SUGGESTION_FORMATS)
    .describe("Default presentation format. This control is always shown."),
});
export type ReportPredefinedQuestions = z.infer<
  typeof reportPredefinedQuestionsSchema
>;

export const DEFAULT_PREDEFINED_QUESTIONS: ReportPredefinedQuestions = {
  timePeriod: "l30",
  channels: [],
  comparison: "previous-period",
  format: "detailed",
};

export const reportSuggestionCardSchema = z.object({
  headline: z
    .string()
    .min(1)
    .describe(
      "Short label naming the report, e.g. 'Weekly paid-media ROAS digest'. Used as the suggested report name.",
    ),
  category: z
    .enum(REPORT_SUGGESTION_CATEGORIES)
    .describe("Opinionation bucket this suggestion belongs to."),
  reason: z
    .string()
    .min(1)
    .describe(
      "One-line explanation of why this report is worth running for this workspace (rendered on the card).",
    ),
  outcomes: z
    .array(z.string().min(1))
    .describe(
      "2-3 bullet points describing what the report will contain (rendered on the card).",
    ),
  prompt: z
    .string()
    .min(1)
    .describe(
      "A standing report instruction, sent verbatim as the report's prompt: what to summarize, how to structure it, what to flag. Should read as a recurring deliverable, not a one-off question.",
    ),
  suggestedCadence: z
    .enum(REPORT_SUGGESTION_CADENCES)
    .nullable()
    .describe("Suggested run cadence for the report, or null if unsure."),
  predefinedQuestions: reportPredefinedQuestionsSchema.describe(
    "The standard controls the user can customize before creating this report: each control's default value, or null to hide it.",
  ),
  parentModelId: z.coerce
    .string()
    .nullable()
    .describe(
      "The Customer Studio parent model id (as a string) this report revolves around, or null. Use an exact id from the workspace bundle; otherwise null. Numeric ids are coerced to string.",
    ),
  questions: z
    .array(reportSuggestionQuestionSchema)
    .max(3)
    .default([])
    .describe(
      "Optional clarifying questions specific to this report suggestion. Prefer 0-3 high-signal questions over generic setup questions.",
    ),
});
export type ParsedReportSuggestion = z.infer<typeof reportSuggestionCardSchema>;

export const reportSuggestionsResponseSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("no_data"),
    reason: z
      .string()
      .nullable()
      .describe(
        "Short human-readable explanation of why there are no suggestions.",
      ),
  }),
  z.object({
    status: z.literal("ok"),
    suggestions: z
      .array(reportSuggestionCardSchema)
      .describe(
        `The full set of report suggestions in a single response (aim for 8-${MAX_REPORT_SUGGESTIONS} strong cards).`,
      ),
  }),
]);
export type ReportSuggestionsResponse = z.infer<
  typeof reportSuggestionsResponseSchema
>;

export const storedReportSuggestionQuestionSchema =
  reportSuggestionQuestionSchema.extend({
    id: z
      .string()
      .min(1)
      .describe("Stable per-question id assigned server-side at persist time."),
  });
export type StoredReportSuggestionQuestion = z.infer<
  typeof storedReportSuggestionQuestionSchema
>;

export const storedReportSuggestionCardSchema = reportSuggestionCardSchema
  .extend({
    id: z
      .string()
      .min(1)
      .describe("Stable per-card id assigned server-side at persist time."),
    // Optional on stored rows: newly generated cards carry `predefinedQuestions`,
    // but rows persisted before the merge instead have the legacy `defaults`
    // (below). The frontend reconstructs the controls from whichever is present
    // — no migration.
    predefinedQuestions: reportPredefinedQuestionsSchema.optional(),
    defaults: reportSuggestionDefaultsSchema.optional(),

    questions: z.array(storedReportSuggestionQuestionSchema).default([]),
  })
  .refine(
    (card) =>
      card.predefinedQuestions !== undefined || card.defaults !== undefined,
    {
      message:
        "Stored report suggestions require predefinedQuestions or legacy defaults.",
    },
  );
export type StoredReportSuggestionCard = z.infer<
  typeof storedReportSuggestionCardSchema
>;

export const contentStoredReportSuggestionsSchema = z.object({
  generated_at: z.string(),
  suggestions: z.array(storedReportSuggestionCardSchema),
});
export const noDataStoredReportSuggestionsSchema = z.object({
  _meta: z.object({
    status: z.literal("no_data"),
    reason: z.string().nullable(),
  }),
});
export const storedReportSuggestionsSchema = z.union([
  contentStoredReportSuggestionsSchema,
  noDataStoredReportSuggestionsSchema,
]);
export type StoredReportSuggestions = z.infer<
  typeof storedReportSuggestionsSchema
>;
